import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import {
    BrowserMultiFormatReader,
    DecodeHintType,
    NotFoundException,
    ChecksumException,
    FormatException,
} from "@zxing/library";

// Converte o retângulo do quadrado-guia (em pixels de tela) para coordenadas
// nativas do vídeo, levando em conta o fator de escala do `object-fit: cover`.
// Usado tanto pelo decodificador (zoom digital real) quanto pelo snapshot de
// diagnóstico, pra garantir que os dois mostrem exatamente a mesma região.
function computeRoiSourceRect(videoEl, viewfinderEl) {
    if (!videoEl || !viewfinderEl || !videoEl.videoWidth) return null;
    const videoRect = videoEl.getBoundingClientRect();
    const roiRect = viewfinderEl.getBoundingClientRect();
    if (!videoRect.width || !videoRect.height || !roiRect.width || !roiRect.height) return null;

    const scale = Math.max(
        videoRect.width / videoEl.videoWidth,
        videoRect.height / videoEl.videoHeight
    );
    const sWidth = Math.min(videoEl.videoWidth, roiRect.width / scale);
    const sHeight = Math.min(videoEl.videoHeight, roiRect.height / scale);

    return {
        sx: (videoEl.videoWidth - sWidth) / 2,
        sy: (videoEl.videoHeight - sHeight) / 2,
        sWidth,
        sHeight,
    };
}

// Sobrescreve o passo de captura de frame do zxing (documentado como
// customizável) pra decodificar só o recorte do quadrado-guia, esticado pro
// tamanho do canvas inteiro — um zoom digital real que aumenta a densidade de
// pixels sobre um QR pequeno, em vez de decodificar o frame inteiro da câmera.
class RoiMultiFormatReader extends BrowserMultiFormatReader {
    constructor(hints, videoRef, viewfinderRef) {
        super(hints);
        this.videoRef = videoRef;
        this.viewfinderRef = viewfinderRef;
    }

    drawFrameOnCanvas(srcElement) {
        const roi = computeRoiSourceRect(this.videoRef.current, this.viewfinderRef.current);
        if (!roi) {
            super.drawFrameOnCanvas(srcElement);
            return;
        }
        // O canvas de captura tem a proporção nativa do vídeo (ex.: 16:9), que quase
        // nunca bate com a do quadrado-guia (1:1) — esticar o recorte pra preencher
        // dWidth/dHeight = canvas inteiro distorce a imagem. Em vez disso, escala o
        // recorte mantendo a proporção (a maior escala que ainda cabe no canvas) e
        // centraliza, como um "fit" em vez de um "stretch".
        const { sx, sy, sWidth, sHeight } = roi;
        const { videoWidth, videoHeight } = srcElement;
        const fitScale = Math.min(videoWidth / sWidth, videoHeight / sHeight);
        const dWidth = sWidth * fitScale;
        const dHeight = sHeight * fitScale;
        super.drawFrameOnCanvas(srcElement, {
            sx,
            sy,
            sWidth,
            sHeight,
            dx: (videoWidth - dWidth) / 2,
            dy: (videoHeight - dHeight) / 2,
            dWidth,
            dHeight,
        });
    }
}

export function CameraScanner({
    onDetected,
    formats,
    tryHarder = true,
    label = "Ler Código",
    icon: Icon = Camera,
    scanningHint = "Aponte a câmera para o código.",
    closeAriaLabel = "Fechar leitor",
    viewfinderStyle = { width: "min(62vw, 62vh)", height: "min(62vw, 62vh)" },
}) {
    const [scanning, setScanning] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [focusRing, setFocusRing] = useState(null);
    const videoRef = useRef(null);
    const viewfinderRef = useRef(null);
    const onDetectedRef = useRef(onDetected);

    const hints = useMemo(() => {
        const map = new Map();
        map.set(DecodeHintType.POSSIBLE_FORMATS, formats);
        map.set(DecodeHintType.TRY_HARDER, tryHarder);
        return map;
    }, [formats, tryHarder]);

    useEffect(() => {
        onDetectedRef.current = onDetected;
    }, [onDetected]);

    useEffect(() => {
        if (!scanning) return;

        let cancelled = false;
        const codeReader = new RoiMultiFormatReader(hints, videoRef, viewfinderRef);

        codeReader
            .decodeFromConstraints(
                {
                    video: {
                        facingMode: "environment",
                        width: { ideal: 1920 },
                        advanced: [{ focusMode: "continuous" }],
                    },
                },
                videoRef.current,
                (result, error) => {
                    if (result && !cancelled) {
                        cancelled = true;
                        codeReader.reset();
                        setScanning(false);
                        onDetectedRef.current(result.getText());
                        return;
                    }
                    // sem "result" o callback ainda dispara a cada frame sem código
                    // encontrado — comportamento normal do scan contínuo, não é falha.
                    // Mas se o erro não for um desses três, o loop de decodificação
                    // do zxing para silenciosamente (sem avisar a UI) — logamos pra
                    // não ficar invisível.
                    if (
                        error &&
                        !(error instanceof NotFoundException) &&
                        !(error instanceof ChecksumException) &&
                        !(error instanceof FormatException)
                    ) {
                        console.error("CameraScanner: erro inesperado durante a decodificação:", error);
                    }
                }
            )
            .catch((e) => {
                if (cancelled) return;
                setScanning(false);
                if (e.name === "NotAllowedError") {
                    setScanError("Permissão de câmera negada.");
                } else if (e.name === "NotFoundError") {
                    setScanError("Nenhuma câmera encontrada.");
                } else {
                    setScanError("Não foi possível acessar a câmera.");
                }
            });

        return () => {
            cancelled = true;
            codeReader.reset();
        };
    }, [scanning, hints]);

    const stopScan = () => {
        setScanning(false);
    };

    const startScan = () => {
        setScanError(null);
        setSnapshot(null);
        setScanning(true);
    };

    // Foco por toque: muitos celulares não travam o foco automático numa
    // distância curta o suficiente pra ler um QR pequeno de nota térmica.
    // Deixar o usuário tocar na tela e pedir pra câmera focar ali (mesmo
    // padrão de apps de câmera nativos) costuma resolver quando o foco
    // contínuo sozinho não trava direito.
    const handleTapToFocus = (e) => {
        const video = videoRef.current;
        const track = video?.srcObject?.getVideoTracks?.()[0];
        if (!track) return;

        const rect = video.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const x = (clientX - rect.left) / rect.width;
        const y = (clientY - rect.top) / rect.height;

        setFocusRing({ x: clientX - rect.left, y: clientY - rect.top });
        setTimeout(() => setFocusRing(null), 800);

        track
            .applyConstraints({ advanced: [{ pointsOfInterest: [{ x, y }] }] })
            .catch(() => {
                // Câmera/navegador não suporta foco por ponto -- silenciosamente ignorado,
                // o foco contínuo padrão continua valendo.
            });
    };

    // Diagnóstico temporário: captura o frame atual da câmera exatamente como o
    // zxing o vê, pra investigar por que a decodificação não está achando o
    // código (foco/nitidez/enquadramento). Remover depois de resolvido.
    const captureSnapshot = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        const roi = computeRoiSourceRect(video, viewfinderRef.current);
        if (roi) {
            // Mesmo recorte que o decodificador vê (zoom digital do quadrado-guia),
            // não o frame inteiro — pra diagnóstico refletir o que é realmente lido.
            // "Fit" em vez de "stretch": mantém a proporção do recorte, sem distorcer.
            const fitScale = Math.min(canvas.width / roi.sWidth, canvas.height / roi.sHeight);
            const dWidth = roi.sWidth * fitScale;
            const dHeight = roi.sHeight * fitScale;
            ctx.drawImage(
                video,
                roi.sx, roi.sy, roi.sWidth, roi.sHeight,
                (canvas.width - dWidth) / 2, (canvas.height - dHeight) / 2, dWidth, dHeight
            );
        } else {
            ctx.drawImage(video, 0, 0);
        }
        setSnapshot({
            url: canvas.toDataURL("image/png"),
            width: video.videoWidth,
            height: video.videoHeight,
        });
    };

    return (
        <div>
            {!scanning ? (
                <button
                    className="btn btn-outline-custom d-flex align-items-center gap-2"
                    type="button"
                    onClick={startScan}
                >
                    <Icon size={16} />
                    {label}
                </button>
            ) : (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1080,
                        backgroundColor: "#000",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <video
                        ref={videoRef}
                        onClick={handleTapToFocus}
                        style={{ flex: 1, width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                        muted
                        playsInline
                    />
                    <div
                        ref={viewfinderRef}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            border: "2px solid rgba(255,255,255,0.9)",
                            borderRadius: "0.5rem",
                            boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
                            pointerEvents: "none",
                            ...viewfinderStyle,
                        }}
                    />
                    {focusRing && (
                        <div
                            style={{
                                position: "absolute",
                                left: focusRing.x - 30,
                                top: focusRing.y - 30,
                                width: 60,
                                height: 60,
                                borderRadius: "50%",
                                border: "2px solid #fff",
                                pointerEvents: "none",
                            }}
                        />
                    )}
                    <button
                        className="btn"
                        type="button"
                        onClick={stopScan}
                        aria-label={closeAriaLabel}
                        style={{
                            position: "absolute",
                            top: "1rem",
                            right: "1rem",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            border: "none",
                            color: "#fff",
                            borderRadius: "50%",
                            width: "2.5rem",
                            height: "2.5rem",
                        }}
                    >
                        <X size={20} />
                    </button>
                    <div
                        style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: "1rem",
                            background: "linear-gradient(transparent, rgba(0,0,0,0.75))",
                        }}
                    >
                        <p className="small text-center mb-2" style={{ color: "#fff" }}>{scanningHint} Encaixe o código no quadro. Toque na tela para focar.</p>
                        <div className="d-flex justify-content-center">
                            <button
                                className="btn btn-sm btn-outline-light"
                                type="button"
                                onClick={captureSnapshot}
                            >
                                Capturar frame atual (diagnóstico)
                            </button>
                        </div>
                        {snapshot && (
                            <div className="mt-2 text-center">
                                <p className="small mb-1" style={{ color: "#fff" }}>
                                    Frame capturado ({snapshot.width}x{snapshot.height}):
                                </p>
                                <img
                                    src={snapshot.url}
                                    alt="Frame capturado da câmera para diagnóstico"
                                    style={{ maxWidth: "100%", maxHeight: "40vh", border: "1px solid #fff", borderRadius: "0.375rem" }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            {scanError && <p className="text-danger-custom small mt-2 mb-0">{scanError}</p>}
        </div>
    );
}
